import { QueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

/**
 * A refusal the server has already made its mind up about.
 *
 * Retrying one costs a second identical round trip and react-query's retry delay
 * on top, and cannot change the answer: a 403 on an endpoint this office may not
 * read is a 403 the next time too, a 404 for a record that is gone stays gone, a
 * 400 for a query the route rejected will be rejected again. Until both attempts
 * had come back the page could not draw its empty state or its error, so the
 * cost landed on exactly the screens that had least to show.
 *
 * 429 is the exception, and the reason this is a list rather than `status < 500`:
 * "too many requests" is a 4xx that explicitly asks to be asked again.
 *
 * An error with no response at all — the request never reached the server — is
 * the case a retry exists for, and keeps it.
 */
function isSettledRefusal(error: Error): boolean {
  if (!isAxiosError(error)) return false
  const status = error.response?.status
  if (status === undefined) return false
  return status >= 400 && status < 500 && status !== 429
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      // One retry, for the failures a retry can fix. See `isSettledRefusal`.
      retry: (failureCount, error) => failureCount < 1 && !isSettledRefusal(error),
      refetchOnWindowFocus: false,
    },
  },
})
